import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import { API } from '../config';
import '../styles/theme.css';

const CATEGORY_ICONS = {
  'Technical Preparation': 'fa-code',
  'Level-Specific Advice': 'fa-layer-group',
  'Company Research': 'fa-building',
  'Skills to Highlight': 'fa-star',
  'Remote Work': 'fa-wifi',
  'Hybrid Work': 'fa-exchange-alt',
  'Salary Negotiation': 'fa-money-bill-wave',
  'Closing Strong': 'fa-flag-checkered',
};

const CATEGORY_COLORS = {
  'Technical Preparation': 'blue',
  'Level-Specific Advice': 'purple',
  'Company Research': 'orange',
  'Skills to Highlight': 'green',
  'Remote Work': 'blue',
  'Hybrid Work': 'orange',
  'Salary Negotiation': 'green',
  'Closing Strong': 'purple',
};

export default function InterviewPrep() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [tips, setTips] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');
  const tier = localStorage.getItem('user_tier') || 'free';
  const isPremium = tier === 'premium';

  // Load the seeker's applied/matched jobs to pick from
  useEffect(() => {
    if (!isPremium) { setJobsLoading(false); return; }
    fetch(`${API}/applications/my-applications/`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const list = Array.isArray(data) ? data : (data.applications || []);
        // Only show jobs where the seeker has an active interview invitation
        const seen = new Set();
        const jobs = [];
        for (const a of list) {
          if (a.job_id && !seen.has(a.job_id) && a.status?.toLowerCase() === 'interview') {
            seen.add(a.job_id);
            jobs.push({ id: a.job_id, title: a.job_title, company: a.job_company });
          }
        }
        setJobs(jobs);
      })
      .catch(() => {})
      .finally(() => setJobsLoading(false));
  }, [isPremium, token]);

  const fetchTips = async (jobId) => {
    setLoading(true); setError(''); setTips(null);
    try {
      const res = await fetch(`${API}/insights/interview-prep?job_id=${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Failed to load tips');
      setTips(json);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleJobSelect = (job) => {
    setSelectedJob(job);
    fetchTips(job.id);
  };

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Interview Prep</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header">
            <h1>
              Interview Prep{' '}
              <span className="badge badge-purple" style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}>Premium</span>
            </h1>
            <p>AI-generated interview tips tailored to each job you've applied for</p>
          </div>

          {!isPremium ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <i className="fas fa-lock" style={{ fontSize: '3rem', color: 'var(--text-muted)', marginBottom: '1rem', display: 'block' }} />
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Premium Feature</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                Interview prep tips are available on the Premium plan (R599/month).
              </p>
              <button className="btn btn-primary" onClick={() => navigate('/pricing')}>
                <i className="fas fa-crown"></i> Upgrade to Premium
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1.5rem', alignItems: 'start' }}>
              {/* Job selector */}
              <div className="card">
                <div className="card-header">
                  <span className="card-title">Your Applications</span>
                </div>
                {jobsLoading ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                ) : jobs.length === 0 ? (
                  <div style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    No interview invitations yet. Once a recruiter invites you to an interview, the job will appear here.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.5rem 0' }}>
                    {jobs.map(job => (
                      <button
                        key={job.id}
                        onClick={() => handleJobSelect(job)}
                        style={{
                          textAlign: 'left', padding: '0.75rem 1rem', border: 'none', borderRadius: 8,
                          cursor: 'pointer', transition: 'background 0.15s',
                          background: selectedJob?.id === job.id ? 'var(--primary-light, rgba(93,26,112,0.12))' : 'transparent',
                          color: selectedJob?.id === job.id ? 'var(--primary)' : 'var(--text-primary)',
                          borderLeft: selectedJob?.id === job.id ? '3px solid var(--primary)' : '3px solid transparent',
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{job.title}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{job.company}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Tips panel */}
              <div>
                {!selectedJob && !loading && (
                  <div className="empty-state">
                    <i className="fas fa-chalkboard-teacher"></i>
                    <h3>Select a job</h3>
                    <p>Pick an application on the left to get AI-generated interview tips</p>
                  </div>
                )}

                {loading && (
                  <div className="empty-state">
                    <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
                    <p>Generating your tips...</p>
                  </div>
                )}

                {error && (
                  <div className="alert alert-error"><i className="fas fa-exclamation-circle"></i> {error}</div>
                )}

                {tips && !loading && (
                  <>
                    <div className="card" style={{ marginBottom: '1rem', padding: '1.25rem 1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div className="stat-icon-wrap purple"><i className="fas fa-briefcase"></i></div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)' }}>{tips.job_title}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{tips.company}</div>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {tips.tips.map((tip, i) => {
                        const icon = CATEGORY_ICONS[tip.category] || 'fa-lightbulb';
                        const color = CATEGORY_COLORS[tip.category] || 'blue';
                        return (
                          <div key={i} className="card" style={{ padding: '1.25rem 1.5rem' }}>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                              <div className={`stat-icon-wrap ${color}`} style={{ flexShrink: 0, marginTop: 2 }}>
                                <i className={`fas ${icon}`}></i>
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  {tip.category}
                                </div>
                                <p style={{ color: 'var(--text-primary)', margin: 0, lineHeight: 1.6 }}>{tip.tip}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
